import { gql } from '@apollo/client';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { MutableRefObject, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { fromError } from '~utils/errors';
import { useCreatePlaylistMutation } from './__generated__/CreatePlaylistModal.graphql';

const FormData = z.object({
  customerId: z.string(),
  title: z.string().min(1),
  description: z.string().optional(),
});
type FormData = z.infer<typeof FormData>;

interface Props {
  customerId: string;
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: (id: string) => Promise<void> | void;
}

export function CreatePlaylistModal({ customerId, isOpen, onCancel, onSuccess }: Props) {
  const initialFocusRef = useRef<HTMLInputElement | null>(null);

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent>
        <CreatePlaylistModalContent
          customerId={customerId}
          initialFocusRef={initialFocusRef}
          onCancel={onCancel}
          onSuccess={onSuccess}
        />
      </ModalContent>
    </Modal>
  );
}

type ContentProps = {
  customerId: string;
  initialFocusRef: MutableRefObject<HTMLInputElement | null>;
  onCancel: () => void;
  onSuccess: (id: string) => Promise<void> | void;
};

function CreatePlaylistModalContent({
  customerId,
  initialFocusRef,
  onCancel,
  onSuccess,
}: ContentProps) {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      customerId,
      title: '',
    },
    resolver: zodResolver(FormData),
  });
  const { ref: titleInputRef, ...titleInputProps } = register('title');

  const [createPlaylist] = useCreatePlaylistMutation();

  const onSubmit = useCallback(
    async ({ customerId, title, description }: FormData) => {
      try {
        const { data } = await createPlaylist({
          variables: {
            input: {
              customerId,
              title,
              description,
            },
          },
        });

        if (!data || !data.playlistCreate.playlist) {
          throw new Error('Create playlist failed');
        }

        await onSuccess?.(data.playlistCreate.playlist.id);
      } catch (err) {
        toast({
          status: 'error',
          title: 'Cannot create playlist',
          description: fromError(err, 'CreatePlaylist'),
        });
      }
    },
    [toast, createPlaylist, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalHeader>Create your new playlist</ModalHeader>
      <ModalCloseButton onClick={onCancel} />
      <ModalBody>
        <VStack>
          <FormControl isRequired isInvalid={Boolean(errors.title)}>
            <FormLabel>Playlist title</FormLabel>
            <Input
              ref={(r) => {
                titleInputRef(r);
                initialFocusRef.current = r;
              }}
              placeholder="New playlist title"
              {...titleInputProps}
            />
            <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.description)}>
            <FormLabel>Description</FormLabel>
            <Input
              type="textarea"
              placeholder="New playlist description"
              {...register('description')}
            />
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>
        </VStack>
      </ModalBody>

      <ModalFooter>
        <Button variant="ghost" colorScheme="blue" onClick={onCancel} isDisabled={isSubmitting}>
          Close
        </Button>
        <Button
          variant="solid"
          colorScheme="blue"
          marginLeft="3"
          type="submit"
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
        >
          Create
        </Button>
      </ModalFooter>
    </form>
  );
}

CreatePlaylistModal.graphql = {
  mutations: {
    CreatePlaylist: gql`
      mutation CreatePlaylist($input: PlaylistCreateInput!) {
        playlistCreate(input: $input) {
          playlist {
            id
            ...PlaylistTable_playlist
          }
        }
      }
    `,
  },
};
